import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Bell,
  AlertTriangle,
  CheckCircle,
  Info,
  Clock,
  Filter,
  Check,
  Settings,
  Trash2,
  RefreshCw,
  User,
  CreditCard,
  GraduationCap,
  Building
} from 'lucide-react'

interface Notification {
  id: string
  type: 'info' | 'warning' | 'success' | 'error'
  title: string
  message: string
  timestamp: string
  isRead: boolean
  category: 'system' | 'payment' | 'student' | 'college'
  priority: 'low' | 'medium' | 'high'
  actionUrl?: string
}

const Notifications = () => {
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all')
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'system' | 'payment' | 'student' | 'college'>('all')
  
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'warning',
      title: 'Payment Gateway Maintenance',
      message: 'Scheduled maintenance on payment gateway from 2:00 AM to 4:00 AM IST tonight. Students may experience payment delays.',
      timestamp: '2024-12-15T10:30:00',
      isRead: false,
      category: 'system',
      priority: 'high',
      actionUrl: '/settings'
    },
    {
      id: '2',
      type: 'info',
      title: 'New Student Enrollment',
      message: '15 new students have been enrolled across Engineering and Commerce colleges today.',
      timestamp: '2024-12-15T09:15:00',
      isRead: false,
      category: 'student',
      priority: 'medium'
    },
    {
      id: '3',
      type: 'success',
      title: 'Monthly Fee Collection Complete',
      message: 'November 2024 fee collection target of ₹45,00,000 has been achieved with 98.5% collection rate.',
      timestamp: '2024-12-15T08:00:00',
      isRead: true,
      category: 'payment',
      priority: 'medium'
    },
    {
      id: '4',
      type: 'warning',
      title: 'Outstanding Dues Alert',
      message: '247 students have outstanding dues exceeding ₹50,000. Reminder emails have been sent.',
      timestamp: '2024-12-14T16:45:00',
      isRead: false,
      category: 'payment',
      priority: 'high'
    },
    {
      id: '5',
      type: 'info',
      title: 'System Backup Completed',
      message: 'Daily database backup completed successfully. Backup size: 2.4 GB',
      timestamp: '2024-12-14T03:00:00',
      isRead: true,
      category: 'system',
      priority: 'low'
    },
    {
      id: '6',
      type: 'error',
      title: 'Failed Login Attempts',
      message: 'Multiple failed login attempts detected from IP 192.168.1.100. Account has been temporarily locked.',
      timestamp: '2024-12-13T14:20:00',
      isRead: false,
      category: 'system',
      priority: 'high'
    },
    {
      id: '7',
      type: 'success',
      title: 'New College Registration',
      message: 'Adarsha Institute of Technology has been successfully registered and activated in the system.',
      timestamp: '2024-12-12T11:30:00',
      isRead: true,
      category: 'college',
      priority: 'medium'
    }
  ])

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, isRead: true } : notif
      )
    )
  }

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, isRead: true }))
    )
  }

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id))
  }

  const getFilteredNotifications = () => {
    return notifications.filter(notif => {
      const matchesReadFilter = 
        filter === 'all' || 
        (filter === 'read' && notif.isRead) || 
        (filter === 'unread' && !notif.isRead)
      
      const matchesCategoryFilter = 
        categoryFilter === 'all' || notif.category === categoryFilter
      
      return matchesReadFilter && matchesCategoryFilter
    })
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-600" />
      case 'error': return <AlertTriangle className="h-5 w-5 text-red-600" />
      default: return <Info className="h-5 w-5 text-blue-600" />
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'payment': return <CreditCard className="h-4 w-4" />
      case 'student': return <GraduationCap className="h-4 w-4" />
      case 'college': return <Building className="h-4 w-4" />
      default: return <Settings className="h-4 w-4" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 48) return 'Yesterday'
    return date.toLocaleDateString()
  }

  const unreadCount = notifications.filter(n => !n.isRead).length
  const filteredNotifications = getFilteredNotifications()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Bell className="h-8 w-8 text-blue-600" />
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white">
                {unreadCount} new
              </Badge>
            )}
          </h1>
          <p className="text-gray-600">Stay updated with system alerts and important information</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            className="flex items-center space-x-2"
          >
            <CheckCircle className="h-4 w-4" />
            <span>Mark All Read</span>
          </Button>
          
          <Button variant="outline" className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filter by:</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {['all', 'unread', 'read'].map((filterOption) => (
                <Button
                  key={filterOption}
                  variant={filter === filterOption ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(filterOption as any)}
                  className="capitalize"
                >
                  {filterOption}
                </Button>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-2">
              {['all', 'system', 'payment', 'student', 'college'].map((categoryOption) => (
                <Button
                  key={categoryOption}
                  variant={categoryFilter === categoryOption ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCategoryFilter(categoryOption as any)}
                  className="flex items-center space-x-1 capitalize"
                >
                  {categoryOption !== 'all' && getCategoryIcon(categoryOption)}
                  <span>{categoryOption}</span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <div className="space-y-4">
        <AnimatePresence>
          {filteredNotifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`transition-all hover:shadow-md ${
                !notification.isRead ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''
              }`}>
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {getTypeIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className={`font-semibold ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                              {notification.title}
                            </h3>
                            <div className="flex items-center space-x-2">
                              <Badge variant="outline" className={getPriorityColor(notification.priority)}>
                                {notification.priority}
                              </Badge>
                              <Badge variant="outline" className="flex items-center space-x-1">
                                {getCategoryIcon(notification.category)}
                                <span className="capitalize">{notification.category}</span>
                              </Badge>
                            </div>
                          </div>
                          
                          <p className={`text-sm ${!notification.isRead ? 'text-gray-800' : 'text-gray-600'} mb-3`}>
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>{formatTimestamp(notification.timestamp)}</span>
                            </div>
                            {!notification.isRead && (
                              <Badge variant="outline" className="bg-blue-100 text-blue-800">
                                New
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          {!notification.isRead && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                              className="flex items-center space-x-1"
                            >
                              <CheckCircle className="h-3 w-3" />
                              <span>Mark as read</span>
                            </Button>
                          )}
                          
                          {notification.actionUrl && (
                            <Button variant="outline" size="sm">
                              View Details
                            </Button>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNotification(notification.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {filteredNotifications.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications found</h3>
            <p className="text-gray-600">
              {filter === 'unread' 
                ? "You're all caught up! No unread notifications."
                : "Try adjusting your filters to see more notifications."
              }
            </p>
          </motion.div>
        )}
      </div>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Notification Preferences</span>
          </CardTitle>
          <CardDescription>Configure how you receive notifications</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Email Notifications</h4>
              <div className="space-y-3">
                {[
                  { id: 'payment-alerts', label: 'Payment Alerts', enabled: true },
                  { id: 'system-updates', label: 'System Updates', enabled: true },
                  { id: 'security-alerts', label: 'Security Alerts', enabled: true },
                  { id: 'daily-summary', label: 'Daily Summary', enabled: false }
                ].map((setting) => (
                  <div key={setting.id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{setting.label}</span>
                    <Button
                      variant={setting.enabled ? "default" : "outline"}
                      size="sm"
                    >
                      {setting.enabled ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Push Notifications</h4>
              <div className="space-y-3">
                {[
                  { id: 'instant-alerts', label: 'Instant Alerts', enabled: true },
                  { id: 'daily-digest', label: 'Daily Digest', enabled: true },
                  { id: 'weekend-summary', label: 'Weekend Summary', enabled: false }
                ].map((setting) => (
                  <div key={setting.id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{setting.label}</span>
                    <Button
                      variant={setting.enabled ? "default" : "outline"}
                      size="sm"
                    >
                      {setting.enabled ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default Notifications
